import { Box, ButtonBase, SxProps, TableCell, TableHead, TableRow, TableSortLabel, Theme } from "@mui/material"
import { Conditional, Stack } from "src/shared/components"
import FilterDataGrid from "./filter-datagrid"
import FilterListIcon from "@mui/icons-material/FilterList"
import { flexRender, Header, Table } from "@tanstack/react-table"
import { getStickyColumnOffset } from "./data-grid-utils"

const EnhancedMuiThead = ({
  table,
  setAnchorEl,
  stripBorder,
  anchorEl,
  filterRef,
  scrolled = { left: false, right: false },
  id
}: {
  table: Table<any>
  setAnchorEl: any
  anchorEl: any
  stripBorder: any
  filterRef: any
  scrolled?: { left?: boolean; right?: boolean }
  id?: string
}) => {
  return (
    <TableHead sx={{ zIndex: 2, position: "relative" }} className={`${id}-data-grid-custom-header`}>
      {table.getHeaderGroups().map((headerGroup) => {
        return (
          <TableRow
            sx={{
              zIndex: 1,
              position: "sticky",
              top: 0,
              background: (theme) => theme.palette.background.default,
              borderBottom: (theme) => `1px solid ${theme.palette.table?.border}`
            }}
            className={`${id}-data-grid-custom-header-row`}
            key={headerGroup.id}
          >
            {headerGroup.headers.map((header, index: number) => {
              const commonStickyStyles = getCommonStickyStyles({
                headers: headerGroup.headers,
                sticky: header.column.columnDef?.meta?.sticky,
                index,
                isScrolledLeft: scrolled.left,
                isScrolledRight: scrolled.right
              })
              const headerContent = flexRender(header.column.columnDef.header, header.getContext())
              const headerText = typeof headerContent === "string" ? headerContent : null

              return (
                <TableCell
                  className={`${id}-data-grid-custom-header-cell`}
                  key={header.id}
                  colSpan={header.colSpan}
                  align={header.column.columnDef.meta?.headerAlign}
                  sx={{
                    background: (theme) => theme.palette.table?.headerBackground,
                    width: header.column.columnDef?.meta?.width ? header.column.columnDef.meta.width : "auto",
                    minWidth: header.column.columnDef?.meta?.minWidth ? header.column.columnDef.meta.minWidth : "auto",
                    maxWidth: header.column.columnDef?.meta?.maxWidth ? header.column.columnDef.meta.maxWidth : "500px",
                    borderLeft: (theme) => (index > 0 ? `1px solid ${theme.palette.table?.border}` : undefined),
                    ...(stripBorder && { borderBottom: (theme) => `1px solid ${theme.palette.table?.border}` }),
                    ...commonStickyStyles
                  }}
                  sortDirection={header.column.getIsSorted() ?? false}
                >
                  {header.isPlaceholder ? null : (
                    <Stack
                      direction="row"
                      alignItems={"center"}
                      justifyContent={header.column.columnDef.meta?.headerAlign}
                      sx={{ color: (theme) => theme.palette.table?.headerColor }}
                    >
                      {header.column?.getCanSort() ? (
                        <TableSortLabel
                          active={!!header.column?.getIsSorted()}
                          direction={
                            header.column?.getIsSorted() ? (header.column?.getIsSorted() as "asc" | "desc") : "asc"
                          }
                          onClick={() => header.column?.toggleSorting()}
                          sx={{ fontWeight: 500 }}
                        >
                          {/* <Tooltip arrow title={headerText}> */}
                          <Box
                            sx={{
                              width: "100%",
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                            title={headerText || undefined}
                          >
                            {headerContent}
                          </Box>
                          {/* </Tooltip> */}
                          {header.column?.getIsSorted() && (
                            <Box
                              component="span"
                              sx={{
                                position: "absolute",
                                width: "1px",
                                height: "1px",
                                margin: "-1px",
                                border: "0",
                                padding: "0",
                                clip: "rect(0 0 0 0)",
                                overflow: "hidden",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {header.column?.getIsSorted() ? "sorted descending" : "sorted ascending"}
                            </Box>
                          )}
                        </TableSortLabel>
                      ) : headerText ? (
                        <Box
                          sx={{
                            width: "100%",
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                          title={headerText || undefined}
                        >
                          {headerText}
                        </Box>
                      ) : (
                        <span>{headerContent}</span>
                      )}

                      <Conditional condition={header.column.getCanFilter() || header.column.getCanSort()}>
                        <ButtonBase
                          id={header.column.id}
                          sx={{
                            opacity: header.column.getIsFiltered() ? 1 : 0.2,
                            "&:hover": { opacity: 1 }
                          }}
                          ref={(comp) => (filterRef.current[index] = comp)}
                          onClick={() => {
                            setAnchorEl(filterRef.current[index])
                          }}
                        >
                          <FilterListIcon
                            sx={{ fontSize: (theme) => theme.typography.body1.fontSize }}
                            color="inherit"
                          />
                        </ButtonBase>

                        {anchorEl?.id === header.column.id && (
                          <FilterDataGrid
                            key={header.column.id}
                            anchorEl={anchorEl}
                            column={header.column}
                            setSorting={(val: any) => header.column.toggleSorting(val)}
                            handleClose={() => {
                              setAnchorEl(null)
                            }}
                          />
                        )}
                      </Conditional>
                    </Stack>
                  )}
                </TableCell>
              )
            })}
          </TableRow>
        )
      })}
    </TableHead>
  )
}

export default EnhancedMuiThead

export const getCommonStickyStyles = ({
  headers,
  sticky,
  index,
  isScrolledLeft,
  isScrolledRight
}: {
  headers: Header<any, any>[]
  sticky?: "left" | "right"
  index: number
  isScrolledLeft?: boolean
  isScrolledRight?: boolean
}): SxProps<Theme> => {
  if (!sticky) return {}

  const stickyHeaders = headers.reduce(
    (acc: any, cell, index) => {
      const sticky = cell.column.columnDef?.meta?.sticky
      if (sticky) {
        acc[sticky] = acc[sticky] || []
        acc[sticky].push({ index, width: cell.column.columnDef?.meta?.width })
      }
      return acc
    },
    { left: [], right: [] }
  )

  const stickyOffset = sticky
    ? getStickyColumnOffset(index, sticky === "left" ? stickyHeaders.left : stickyHeaders.right, sticky)
    : "0px"
  const showShadow =
    stickyHeaders.right[0]?.index === index || stickyHeaders.left[stickyHeaders.left.length - 1]?.index === index
  return {
    position: "sticky",
    [sticky]: stickyOffset,
    zIndex: 2,
    background: (theme) => theme.palette.table?.background ?? "red",
    boxShadow: (theme) =>
      isScrolledLeft && sticky === "left" && showShadow
        ? `2px 0 5px -2px ${theme.palette.table?.border}`
        : isScrolledRight && sticky === "right" && showShadow
          ? `-2px 0 5px -2px ${theme.palette.table?.border}`
          : ""
  }
}
